import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { PageSpinner } from '@/components/ui/Spinner'

interface ProtectedRouteProps {
  /** Required role(s) to access this route */
  roles?: string[]
  /** Required permission string (e.g., "users:read") */
  permission?: string
  /** Redirect path if unauthenticated */
  redirectTo?: string
  /** Redirect path if unauthorized (authenticated but wrong role) */
  unauthorizedRedirect?: string
}

/**
 * ProtectedRoute - redirects to /login if not authenticated.
 * Optionally enforces role-based or permission-based access.
 */
export function ProtectedRoute({
  roles,
  permission,
  redirectTo = '/login',
  unauthorizedRedirect = '/dashboard',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore()
  const location = useLocation()

  // While loading auth state from storage, show spinner
  if (isLoading) {
    return <PageSpinner message="Loading..." />
  }

  // Not authenticated → redirect to login, preserving return URL
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />
  }

  // Role check
  if (roles && roles.length > 0 && user?.role) {
    const userRole = user.role.name.toLowerCase()
    const hasRole = roles.some((r) => r.toLowerCase() === userRole)
    if (!hasRole) {
      return <Navigate to={unauthorizedRedirect} replace />
    }
  }

  // Permission check
  if (permission && user?.role?.permissions) {
    const [resource, action] = permission.split(':')
    const adminRoles = ['admin', 'super_admin', 'superadmin']
    const isAdmin = adminRoles.includes(user.role.name.toLowerCase())

    if (!isAdmin) {
      const hasPermission = user.role.permissions.some(
        (p) =>
          (p.resource === resource || p.resource === '*') &&
          (p.action === action || p.action === '*')
      )
      if (!hasPermission) {
        return <Navigate to={unauthorizedRedirect} replace />
      }
    }
  }

  return <Outlet />
}

/**
 * AdminRoute - only accessible to admin roles
 */
export function AdminRoute() {
  return (
    <ProtectedRoute
      // org_admin is the tenant administrator and must reach the admin pages
      // (Users, Roles…). Without it, org_admins get bounced to /dashboard.
      roles={['admin', 'super_admin', 'superadmin', 'org_admin']}
      unauthorizedRedirect="/dashboard"
    />
  )
}

/**
 * GuestRoute - redirects to dashboard if already authenticated
 * Used for login, register, forgot-password pages
 */
export function GuestRoute({ redirectTo = '/home' }: { redirectTo?: string }) {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  // Never show spinner here — login button already has loading state.
  // Showing a spinner unmounts the form and clears typed email/password on failure.

  // If authenticated, redirect away from guest pages
  if (isAuthenticated) {
    const from = (location.state as { from?: string })?.from || redirectTo
    return <Navigate to={from} replace />
  }

  return <Outlet />
}

export default ProtectedRoute
